import { Popover } from "@base-ui/react/popover";
import {
  addDays,
  addMonths,
  addYears,
  compareAsc,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { vars } from "../../theme/theme.css";
import { fillDaysArray } from "../../utils/functions";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { IconArrowDropDown, IconCalendarCheck } from "../icons";
import { Spinner } from "../Spinner";
import {
  CalendarInputs,
  CalendarOptions,
  type OPTIONS_FUNCTIONS,
  TimeInput,
} from "./CalendarComponents";
import * as styles from "./styles.css";

export interface DateSelected {
  start: Date;
  end: Date;
}

interface CalendarProps extends React.HTMLAttributes<HTMLButtonElement> {
  hideOptions?: Array<keyof typeof OPTIONS_FUNCTIONS>;
  hideAllOptions?: boolean;
  onlyDate?: boolean;
  onSelectDate?: (date: DateSelected) => void;
  defaultDate?: DateSelected;
  isLoading?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value?: DateSelected;
  containerStyles?: React.CSSProperties;
  useTimeRange?: boolean;
  onFilterIntervalChange?: (filterInterval: boolean) => void;
}

const DayButtonComponent = React.memo(function DayButton({
  $isSelected,
  $isHovered,
  $isFirst,
  $isLast,
  $isThisMonth,
  children,
  className,
  ...props
}: any) {
  const classNames = [styles.dayButton];

  if ($isSelected) {
    classNames.push(styles.dayButtonSelected);
  }
  if ($isHovered) {
    classNames.push(styles.dayButtonHovered);
  }
  if (!$isThisMonth) {
    classNames.push(styles.dayButtonOtherMonth);
  }
  if (($isSelected || $isHovered) && !$isFirst && !$isLast) {
    classNames.push(styles.dayButtonBetween);
  }
  if ($isFirst && !$isLast) {
    classNames.push(styles.dayButtonFirst);
  }
  if ($isLast && !$isFirst) {
    classNames.push(styles.dayButtonLast);
  }

  return (
    <button className={classNames.join(" ")} {...props}>
      {children}
    </button>
  );
});

const DayButtonMemo = React.memo(DayButtonComponent);

export const Calendar = forwardRef(function Calendar(
  {
    hideOptions = [],
    hideAllOptions,
    onlyDate,
    onSelectDate,
    defaultDate,
    isLoading,
    disabled,
    minDate,
    maxDate,
    value,
    containerStyles,
    useTimeRange,
    onFilterIntervalChange,
    ...props
  }: CalendarProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const [show, setShow] = useState(false);

  const [selectedDate, setSelectedDate] = useState<DateSelected>(
    defaultDate || {
      start: startOfDay(new Date()),
      end: endOfDay(new Date()),
    },
  );
  const [dateOnButton, setDateOnButton] = useState<DateSelected>(
    defaultDate || {
      start: startOfDay(new Date()),
      end: endOfDay(new Date()),
    },
  );

  const toggleShow = useCallback(() => {
    setSelectedDate(dateOnButton);
    setShow((state) => !state);
  }, [dateOnButton]);

  const [isSelecting, setIsSelecting] = useState(false);
  const [hoveredBoundary, setHoveredBoundary] = useState<Date | null>(null);

  const [monthToShow, setMonthToShow] = useState({
    start: startOfMonth(selectedDate.start),
    end: endOfMonth(selectedDate.start),
  });

  const [focusedDate, setFocusedDate] = useState<Date>(selectedDate.start);
  const gridRef = useRef<HTMLDivElement>(null);
  const focusPendingRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusPendingRef.current) return;
    focusPendingRef.current = false;
    const btn = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${format(focusedDate, "yyyy-MM-dd")}"]`,
    );
    btn?.focus({ preventScroll: true });
  });

  const [firstDay, lastDay] = useMemo(() => {
    if (
      hoveredBoundary &&
      compareAsc(hoveredBoundary, selectedDate.start) < 0
    ) {
      return [hoveredBoundary, selectedDate.end];
    }
    if (
      hoveredBoundary &&
      compareAsc(hoveredBoundary, selectedDate.start) > 0
    ) {
      return [selectedDate.start, hoveredBoundary];
    }
    return [selectedDate.start, selectedDate.end];
  }, [hoveredBoundary, selectedDate.start, selectedDate.end]);

  const onClickDay = useCallback(
    (day: Date) => {
      setFocusedDate(day);
      setHoveredBoundary(null);
      if (!isSelecting) {
        setIsSelecting(true);
        setSelectedDate({
          start: startOfDay(day),
          end: endOfDay(day),
        });
        return;
      }

      if (compareAsc(day, selectedDate.start) > 0) {
        setSelectedDate({
          start: selectedDate.start,
          end: endOfDay(day),
        });
      } else {
        setSelectedDate({
          start: startOfDay(day),
          end: selectedDate.end,
        });
      }
      setIsSelecting(false);
    },
    [isSelecting, selectedDate],
  );

  const daysToShowMemo = useMemo(() => {
    const daysOfTheMonth = eachDayOfInterval(monthToShow);
    fillDaysArray(daysOfTheMonth);

    return daysOfTheMonth.map((day) => ({
      value: day,
      day: day.getDate(),
      month: day.getMonth(),
      year: day.getFullYear(),
      weekday: day.getDay(),
    }));
  }, [monthToShow]);

  const SLIDER_WIDTH = !hideAllOptions ? Number(250) : Number(90);

  const hoursToTime = (h: number) => {
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };
  const timeToHours = (date?: Date) =>
    date ? date.getHours() + date.getMinutes() / 60 : 0;

  const [filterInterval, setFilterInterval] = useState(false);
  const [min, setMin] = useState<number>(timeToHours(selectedDate.start));
  const [max, setMax] = useState<number>(timeToHours(selectedDate.end));

  const getThumbLeft = (value: number) => {
    const sliderWidth = SLIDER_WIDTH;
    return (value / 24) * sliderWidth;
  };

  const handleSelectDate = useCallback(
    (date: DateSelected) => {
      onSelectDate?.(date);
      setDateOnButton(date);
      onFilterIntervalChange?.(filterInterval);
      setShow(false);
    },
    [onSelectDate, filterInterval, onFilterIntervalChange],
  );

  useEffect(() => {
    if (show) {
      setMin(timeToHours(selectedDate.start));
      setMax(timeToHours(selectedDate.end));
    }
  }, [show, selectedDate.start, selectedDate.end, timeToHours]);

  const [activeThumb, setActiveThumb] = useState<"min" | "max" | null>(null);

  const updateDateBoundary = (type: "start" | "end", rawValue: number) => {
    let value = rawValue;

    if (type === "start") {
      value = Math.min(value, max - 0.25);
      setMin(value);

      const startDate = new Date(selectedDate.start);
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);
      startDate.setHours(hours, minutes, 0, 0);

      setSelectedDate({ start: startDate, end: selectedDate.end });
      setActiveThumb("min");
    } else {
      value = Math.min(value, 23.99);
      value = Math.max(value, min + 0.25);
      setMax(value);

      const endDate = new Date(selectedDate.end);
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);

      endDate.setHours(hours, minutes, 0, 0);

      setSelectedDate({ start: selectedDate.start, end: endDate });
      setActiveThumb("max");
    }
  };

  useEffect(() => {
    if (value) {
      setDateOnButton(value);
      setSelectedDate(value);
      setFocusedDate(value.start);
    }
  }, [value]);

  useEffect(() => {
    setMonthToShow({
      start: startOfMonth(selectedDate.start),
      end: endOfMonth(selectedDate.start),
    });
  }, [selectedDate]);

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const NAV_KEYS = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        "Enter",
        " ",
      ];
      if (!NAV_KEYS.includes(e.key)) return;
      e.preventDefault();

      if (e.key === "Enter" || e.key === " ") {
        onClickDay(focusedDate);
        return;
      }

      let next = focusedDate;
      switch (e.key) {
        case "ArrowLeft":
          next = subDays(focusedDate, 1);
          break;
        case "ArrowRight":
          next = addDays(focusedDate, 1);
          break;
        case "ArrowUp":
          next = subDays(focusedDate, 7);
          break;
        case "ArrowDown":
          next = addDays(focusedDate, 7);
          break;
        case "PageUp":
          next = e.shiftKey
            ? subYears(focusedDate, 1)
            : subMonths(focusedDate, 1);
          break;
        case "PageDown":
          next = e.shiftKey
            ? addYears(focusedDate, 1)
            : addMonths(focusedDate, 1);
          break;
        case "Home":
          next = startOfWeek(focusedDate);
          break;
        case "End":
          next = endOfWeek(focusedDate);
          break;
      }

      if (minDate && isBefore(next, startOfDay(minDate)))
        next = startOfDay(minDate);
      if (maxDate && isAfter(next, endOfDay(maxDate))) next = endOfDay(maxDate);

      focusPendingRef.current = true;
      setFocusedDate(next);
      if (!isSameMonth(next, monthToShow.start)) {
        setMonthToShow({
          start: startOfMonth(next),
          end: endOfMonth(next),
        });
      }
    },
    [focusedDate, monthToShow.start, minDate, maxDate, onClickDay],
  );

  return (
    <div className={styles.datePickerWrapper} style={containerStyles} ref={ref}>
      <Popover.Root open={show} onOpenChange={setShow}>
        <Popover.Trigger
          render={
            <button
              className={styles.datePickerTrigger}
              type="button"
              disabled={disabled || isLoading}
              {...props}
            />
          }
          onClick={toggleShow}
        >
          {format(
            dateOnButton.start,
            !onlyDate
              ? "dd/MM/yyyy - HH:mm"
              : useTimeRange
                ? "dd/MM/yyyy - HH:mm"
                : "dd/MM/yyyy",
          )}
          <span>a</span>
          {format(
            dateOnButton.end,
            !onlyDate
              ? "dd/MM/yyyy - HH:mm"
              : useTimeRange
                ? "dd/MM/yyyy - HH:mm"
                : "dd/MM/yyyy",
          )}
          {isLoading ? (
            <Spinner size={20} color={vars.colors.neutral.light} />
          ) : (
            <IconCalendarCheck />
          )}
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="start"
            sideOffset={4}
            className={styles.datePickerPositioner}
          >
            <Popover.Popup
              initialFocus={false}
              finalFocus={false}
              className={styles.datePickerContent}
            >
              <div className={styles.contentBody}>
                {!hideAllOptions && (
                  <CalendarOptions
                    hideOptions={hideOptions}
                    setSelectedDate={setSelectedDate}
                    handleSelectDate={handleSelectDate}
                  />
                )}

                <div className={styles.calendarContainer}>
                  <div className={styles.monthYearHeader}>
                    <IconArrowDropDown
                      style={{ transform: "rotate(90deg)" }}
                      onClick={() => {
                        const newMonth = subMonths(monthToShow.start, 1);

                        setMonthToShow({
                          start: startOfMonth(newMonth),
                          end: endOfMonth(newMonth),
                        });
                      }}
                    />

                    <span>
                      {format(monthToShow.start, "MMMM", { locale: ptBR })}
                      {", "}
                      {format(monthToShow.start, "yyyy")}
                    </span>
                    <IconArrowDropDown
                      style={{ transform: "rotate(-90deg)" }}
                      onClick={() => {
                        const newMonth = addMonths(monthToShow.start, 1);
                        setMonthToShow({
                          start: startOfMonth(newMonth),
                          end: endOfMonth(newMonth),
                        });
                      }}
                    />
                  </div>
                  <ul>
                    <li>D</li>
                    <li>S</li>
                    <li>T</li>
                    <li>Q</li>
                    <li>Q</li>
                    <li>S</li>
                    <li>S</li>
                  </ul>
                  <div
                    className={styles.styledCalendar}
                    ref={gridRef}
                    role="grid"
                    aria-label="Calendário"
                    onKeyDown={handleGridKeyDown}
                  >
                    {daysToShowMemo.map((day) => {
                      return (
                        <DayButtonMemo
                          key={day.value.toString()}
                          data-date={format(day.value, "yyyy-MM-dd")}
                          tabIndex={isSameDay(day.value, focusedDate) ? 0 : -1}
                          aria-label={format(
                            day.value,
                            "d 'de' MMMM 'de' yyyy",
                            { locale: ptBR },
                          )}
                          aria-selected={
                            isAfter(endOfDay(day.value), selectedDate.start) &&
                            isBefore(day.value, selectedDate.end)
                          }
                          onClick={() => onClickDay(day.value)}
                          onMouseEnter={() =>
                            isSelecting && setHoveredBoundary(day.value)
                          }
                          onMouseLeave={() =>
                            isSelecting && setHoveredBoundary(null)
                          }
                          $isHovered={
                            hoveredBoundary !== null &&
                            isAfter(endOfDay(day.value), firstDay) &&
                            isBefore(day.value, endOfDay(lastDay))
                          }
                          $isSelected={
                            isAfter(endOfDay(day.value), selectedDate.start) &&
                            isBefore(day.value, selectedDate.end)
                          }
                          $isFirst={isSameDay(firstDay, day.value)}
                          $isLast={isSameDay(lastDay, day.value)}
                          $isThisMonth={
                            day.month === monthToShow.start.getMonth()
                          }
                          disabled={
                            (minDate && isBefore(day.value, minDate)) ||
                            (maxDate && isAfter(day.value, maxDate))
                          }
                        >
                          {day.day}
                        </DayButtonMemo>
                      );
                    })}
                  </div>

                  {!onlyDate && (
                    <CalendarInputs
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      clearHover={() => setHoveredBoundary(null)}
                      disabled={filterInterval}
                    />
                  )}
                </div>
              </div>

              {useTimeRange && (
                <div
                  className={`${styles.filterIntervalDiary} ${!hideAllOptions ? styles.filterIntervalDiaryFull : styles.filterIntervalDiaryNarrow}`}
                >
                  <Checkbox
                    onChange={(e) => {
                      setFilterInterval(e.target.checked);
                    }}
                    description={
                      filterInterval
                        ? "Horário fixo aplicado a todos os dias selecionados."
                        : ""
                    }
                    label="Filtrar por intervalo (Diário)"
                  />
                  {filterInterval && (
                    <>
                      <div
                        className={styles.containerTime}
                        style={{ position: "relative" }}
                      >
                        <TimeInput
                          type="start"
                          value={selectedDate.start}
                          styled={{
                            position: "absolute",
                            left: `${getThumbLeft(min)}px`,
                          }}
                          setValue={(e) => {
                            setHoveredBoundary(null);
                            setSelectedDate({
                              start: e,
                              end: selectedDate.end,
                            });
                          }}
                        />

                        <TimeInput
                          type="end"
                          value={selectedDate.end}
                          styled={{
                            position: "absolute",
                            left: `${getThumbLeft(max - 24)}px`,
                          }}
                          setValue={(e) => {
                            setHoveredBoundary(null);
                            setSelectedDate({
                              start: selectedDate.start,
                              end: e,
                            });
                          }}
                        />
                      </div>

                      <div className={styles.containerLengthTime}>
                        <div className={styles.sliderContainer}>
                          <div
                            className={styles.range}
                            style={{
                              left: `${(min / 24) * 100}%`,
                              right: `${100 - (max / 24) * 100}%`,
                            }}
                          />
                          <input
                            className={styles.thumb}
                            type="range"
                            min={0}
                            max={23.75}
                            step={0.25}
                            value={min}
                            onChange={(e) =>
                              updateDateBoundary(
                                "start",
                                Number(e.target.value),
                              )
                            }
                            style={{ zIndex: activeThumb === "min" ? 3 : 2 }}
                          />
                          <input
                            className={styles.thumb}
                            type="range"
                            min={0}
                            max={24}
                            step={0.25}
                            value={max}
                            onChange={(e) =>
                              updateDateBoundary("end", Number(e.target.value))
                            }
                            style={{ zIndex: activeThumb === "max" ? 3 : 2 }}
                          />
                        </div>
                      </div>

                      <span className={styles.textHour}>
                        Todos os dias de {hoursToTime(min)} até{" "}
                        {hoursToTime(max)}
                      </span>
                    </>
                  )}
                </div>
              )}

              <div className={styles.contentFooter}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleSelectDate(selectedDate);
                  }}
                >
                  Aplicar
                </Button>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
});

Calendar.displayName = "Calendar";
